import React from 'react';
import { ArrowRight, Cloud, TrendingUp, Brain, Workflow, Cpu, Lock } from 'lucide-react';

const ICON_BG_COLOR = "#3B82F6"; // blue-500
const AI_ICON_BG_COLOR = "#8B5CF6"; // violet-500
const LINK_TEXT_COLOR = "#60A5FA"; // blue-400
const LINK_TEXT_HOVER_COLOR = "#93C5FD"; // blue-300

// Define and export the Service type
export interface Service {
  icon: React.ElementType;
  title: string;
  description: string;
  longDescription: string;
  imageUrl: string;
  link: string;
  category: 'ai' | 'cloud';
}

const servicesData: Service[] = [
  // AI Automation Services (Primary)
  {
    icon: Brain,
    title: "AI Business Process Automation",
    description: "Streamline repetitive tasks, automate document processing, and orchestrate complex workflows with intelligent AI solutions.",
    longDescription:
      '<p>Transform your business operations by automating repetitive, time-consuming tasks that drain your team\'s productivity. Our AI-powered solutions eliminate manual work and reduce human error.</p>' +
      '<h3 class="text-xl font-semibold text-gray-100 mt-4 mb-3">Process Automation Capabilities</h3>' +
      '<ul>' +
      '<li><strong>Document Processing:</strong> Automatically extract, classify, and process documents, invoices, contracts, and forms.<br /><br /></li>' +
      '<li><strong>Workflow Orchestration:</strong> Design intelligent workflows that adapt based on data inputs and business rules.<br /><br /></li>' +
      '<li><strong>Task Automation:</strong> Eliminate manual data entry, email responses, and routine administrative work.<br /><br /></li>' +
      '<li><strong>Integration & Monitoring:</strong> Seamlessly integrate with existing systems and monitor performance with detailed analytics.</li>' +
      '</ul>' +
      '<p>Save 60-80% of time spent on routine tasks while improving accuracy and consistency.</p>',
    imageUrl: "https://picsum.photos/seed/aiprocess/600/400",
    link: "#service-ai-process-automation",
    category: 'ai'
  },
  {
    icon: Cpu,
    title: "Custom AI Solutions",
    description: "Tailored AI applications designed specifically for your business needs, industry requirements, and operational challenges.",
    longDescription:
      '<p>Every business has unique challenges that require custom solutions. We develop bespoke AI applications that address your specific operational needs and industry requirements.</p>' +
      '<h3 class="text-xl font-semibold text-gray-100 mt-4 mb-3">Custom AI Development</h3>' +
      '<ul>' +
      '<li><strong>Industry-Specific Solutions:</strong> AI applications tailored to your sector\'s unique requirements and compliance needs.<br /><br /></li>' +
      '<li><strong>Predictive Analytics:</strong> Forecast trends, predict outcomes, and make data-driven decisions with custom ML models.<br /><br /></li>' +
      '<li><strong>Intelligent Decision Support:</strong> AI systems that analyze complex data and provide actionable insights for decision-making.<br /><br /></li>' +
      '<li><strong>Scalable Architecture:</strong> Built on robust cloud infrastructure to grow with your business needs.</li>' +
      '</ul>' +
      '<p>From concept to deployment, we create AI solutions that deliver measurable business value.</p>',
    imageUrl: "https://picsum.photos/seed/customai/600/400",
    link: "#service-custom-ai",
    category: 'ai'
  },
  {
    icon: Workflow,
    title: "Intelligent Task Automation",
    description: "AI-powered systems that handle routine operational work, freeing your team to focus on strategic, high-value activities.",
    longDescription:
      '<p>Stop wasting valuable human resources on repetitive tasks. Our intelligent automation solutions learn from your processes and handle routine work with greater speed and accuracy than manual operations.</p>' +
      '<h3 class="text-xl font-semibold text-gray-100 mt-4 mb-3">Smart Automation Features</h3>' +
      '<ul>' +
      '<li><strong>Email & Communication Automation:</strong> Intelligent response systems that handle customer inquiries and internal communications.<br /><br /></li>' +
      '<li><strong>Data Processing & Analysis:</strong> Automatically collect, clean, and analyze data from multiple sources.<br /><br /></li>' +
      '<li><strong>Report Generation:</strong> Create comprehensive reports and dashboards automatically based on real-time data.<br /><br /></li>' +
      '<li><strong>Quality Assurance:</strong> AI-powered checking and validation to ensure consistency and accuracy.</li>' +
      '</ul>' +
      '<p>Achieve 24/7 operations with systems that never tire, make fewer errors, and continuously improve.</p>',
    imageUrl: "https://picsum.photos/seed/taskauto/600/400",
    link: "#service-task-automation",
    category: 'ai'
  },
  // AWS Cloud Services (Secondary but important)
  {
    icon: Cloud,
    title: "Cloud Architecture & DevOps",
    description: "Complete AWS infrastructure design, implementation, and automation with CI/CD pipelines and Infrastructure as Code.",
    longDescription:
      '<p>Build scalable, secure, and cost-effective AWS infrastructure with our comprehensive cloud architecture and DevOps services. We handle everything from design to deployment.</p>' +
      '<h3 class="text-xl font-semibold text-gray-100 mt-4 mb-3">Complete Cloud Solutions</h3>' +
      '<ul>' +
      '<li><strong>Infrastructure Design:</strong> Scalable AWS architectures following best practices and Well-Architected Framework.<br /><br /></li>' +
      '<li><strong>DevOps Automation:</strong> CI/CD pipelines, Infrastructure as Code, and automated deployment strategies.<br /><br /></li>' +
      '<li><strong>Migration Services:</strong> Seamless migration from on-premises or other cloud providers to AWS.<br /><br /></li>' +
      '<li><strong>Performance Optimization:</strong> Continuous monitoring and optimization for peak performance.</li>' +
      '</ul>',
    imageUrl: "https://picsum.photos/seed/clouddevops/600/400",
    link: "#service-cloud-devops",
    category: 'cloud'
  },
  {
    icon: Lock,
    title: "Security & Monitoring",
    description: "Comprehensive AWS security implementation and 24/7 monitoring solutions to protect your infrastructure and ensure optimal performance.",
    longDescription:
      '<p>Secure your AWS environment with enterprise-grade security measures and continuous monitoring. We implement layered security and provide round-the-clock oversight.</p>' +
      '<h3 class="text-xl font-semibold text-gray-100 mt-4 mb-3">Security & Monitoring Services</h3>' +
      '<ul>' +
      '<li><strong>Security Implementation:</strong> Multi-layered security architecture, compliance frameworks, and threat protection.<br /><br /></li>' +
      '<li><strong>24/7 Monitoring:</strong> Real-time monitoring, alerting, and incident response for critical infrastructure.<br /><br /></li>' +
      '<li><strong>Compliance Management:</strong> Ensure adherence to industry standards and regulatory requirements.<br /><br /></li>' +
      '<li><strong>Automated Recovery:</strong> Self-healing systems and automated disaster recovery procedures.</li>' +
      '</ul>',
    imageUrl: "https://picsum.photos/seed/securitymon/600/400",
    link: "#service-security-monitoring",
    category: 'cloud'
  },
  {
    icon: TrendingUp,
    title: "Cost Optimization & Support",
    description: "Ongoing AWS cost management, performance optimization, and comprehensive support to maximize your cloud investment ROI.",
    longDescription:
      '<p>Maximize your AWS investment with intelligent cost optimization and ongoing support. We ensure you\'re getting the best value from your cloud infrastructure.</p>' +
      '<h3 class="text-xl font-semibold text-gray-100 mt-4 mb-3">Optimization & Support</h3>' +
      '<ul>' +
      '<li><strong>Cost Analysis:</strong> Detailed AWS spend analysis and identification of cost-saving opportunities.<br /><br /></li>' +
      '<li><strong>Resource Optimization:</strong> Right-sizing, reserved instances, and automated scaling for optimal costs.<br /><br /></li>' +
      '<li><strong>Ongoing Support:</strong> Dedicated support team for maintenance, updates, and continuous improvement.<br /><br /></li>' +
      '<li><strong>Performance Tuning:</strong> Regular performance reviews and optimization recommendations.</li>' +
      '</ul>',
    imageUrl: "https://picsum.photos/seed/costsupport/600/400",
    link: "#service-cost-support",
    category: 'cloud'
  }
];

// Export the services array
export { servicesData as services };

interface ServicesSectionProps {
  onOpenServiceModal: (service: Service) => void;
  onOpenContactModal: () => void;
}

const ServicesSection: React.FC<ServicesSectionProps> = ({ onOpenServiceModal, onOpenContactModal }) => {
  const aiServices = servicesData.filter(service => service.category === 'ai');
  const cloudServices = servicesData.filter(service => service.category === 'cloud');

  const ServiceCard = ({ service }: { service: Service }) => {
    const IconComponent = service.icon;
    const iconBgColor = service.category === 'ai' ? AI_ICON_BG_COLOR : ICON_BG_COLOR;

    return (
      <div className="bg-gray-900 hover:bg-gray-700/50 rounded-lg p-6 transition duration-300 border border-gray-700 flex flex-col">
        <div className="flex-shrink-0">
          <div
            style={{ backgroundColor: iconBgColor }}
            className="flex items-center justify-center h-12 w-12 rounded-md text-white mb-4"
          >
            <IconComponent className="h-6 w-6" />
          </div>
          <h3 className="text-lg font-medium text-white">{service.title}</h3>
          <p className="mt-2 text-base text-gray-300 flex-grow">
            {service.description}
          </p>
        </div>
        <button
          onClick={() => onOpenServiceModal(service)}
          style={{ color: LINK_TEXT_COLOR }}
          onMouseOver={(e) => e.currentTarget.style.color = LINK_TEXT_HOVER_COLOR}
          onMouseOut={(e) => e.currentTarget.style.color = LINK_TEXT_COLOR}
          className="mt-4 text-left inline-flex items-center font-medium transition-colors duration-150 bg-transparent border-none p-0 cursor-pointer">
          Learn more <ArrowRight className="ml-1 h-4 w-4" />
        </button>
      </div>
    );
  };

  return (
    <div id="services" className="py-16 bg-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
            Our AI Automation & Cloud Solutions
          </h2>
          <p className="mt-4 max-w-2xl text-xl text-gray-300 mx-auto">
            Comprehensive AI automation services and AWS cloud infrastructure solutions
          </p>
        </div>

        {/* AI Automation Services */}
        <div className="mt-12">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-white mb-2">AI Automation & Workflows</h3>
            <p className="text-gray-400">Automate business processes and eliminate repetitive tasks</p>
          </div>
          <div className="grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {aiServices.map((service) => (
              <ServiceCard key={service.title} service={service} />
            ))}
          </div>
        </div>

        {/* AWS Cloud Services */}
        <div className="mt-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-white mb-2">AWS Cloud Infrastructure</h3>
            <p className="text-gray-400">Scalable, secure, and cost-effective cloud solutions</p>
          </div>
          <div className="grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {cloudServices.map((service) => (
              <ServiceCard key={service.title} service={service} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicesSection;